import { useCallback } from "react";
import { useFeatureFlag } from "@/query/hooks/useFeatureFlags";

/** The subset of a product any of these call sites can be trusted to have. */
export interface ProductNameFields {
  name?: string | null;
  short_name?: string | null;
}

/**
 * Resolve the label a product is displayed under.
 *
 * `short_name` is the abbreviated label and `name` the full one. Which is the
 * useful one is a tenant decision, not a global truth: for catalogues imported
 * from an ERP the full name is usually the only place the distinguishing
 * detail lives (calibre, origin, packaging, category), and several rows can
 * share one short name — so `name` is the default and `showProductShortName`
 * opts out of it.
 *
 * Either side may be missing, so both directions fall back to the other before
 * giving up. Pure so it can be used outside React (sorting, search indexing,
 * export) — components should reach for {@link useProductDisplayName}, which
 * binds the flag for them.
 */
export function productDisplayName(
  product: ProductNameFields | null | undefined,
  preferShortName: boolean,
): string {
  if (!product) return "";
  const primary = preferShortName ? product.short_name : product.name;
  const fallback = preferShortName ? product.name : product.short_name;
  return primary || fallback || "";
}

/**
 * Flag-bound {@link productDisplayName}. Returns a stable formatter so it can
 * sit in a `useMemo` dependency list without re-running on every render.
 */
export function useProductDisplayName(): (
  product: ProductNameFields | null | undefined,
) => string {
  const preferShortName = useFeatureFlag("showProductShortName") === true;
  return useCallback(
    (product: ProductNameFields | null | undefined) =>
      productDisplayName(product, preferShortName),
    [preferShortName],
  );
}
